import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { commonAuditEvents } from "~/AIEvents/commonAuditEvents";
import { useTrackEvent } from "~/common/audit";
import { commonClientResx } from "../../resxDist/commonClientResx";
import { ButtonCross, ButtonDefault } from "../buttons";
import { useEscapeKeyHandler } from "../hooks/useKeyboardHandler";
import { icon } from "../icons";
import * as styles from "./HeaderSearch.less";
import { Input, inputTheme } from "./Input";
import { InputIconWrapper } from "./inputIcons/InputIconWrapper";
import { InputWrapper } from "./InputWrapper";

export interface SearchProps {
    value: string;
    setSearch: (value: string) => void;
}

export const HeaderSearch: React.FC<SearchProps> = ({ value, setSearch }) => {
    const [showButton, setShowButton] = useState(false);

    useTrackSearch(value);

    useEffect(() => {
        if (value) {
            setSearchMode();
        }
    }, []);

    const setSearchMode = () => {
        setShowButton(true);
    };

    const offSearchMode = () => {
        setSearch("");
        setShowButton(false);
    };

    const escHandler = useEscapeKeyHandler(offSearchMode);

    // hide search input if container (input and close btn) lost focus and user hasn't typed anything in search.
    const { onBlur, elementRef } = useOnContainerBlur(() => {
        const emptySearch = !value.trim();
        if (emptySearch) {
            offSearchMode();
        }
    });

    return (
        <>
            {showButton && (
                <InputWrapper className={styles.search_container} onBlur={onBlur} ref={elementRef}>
                    <Input
                        onKeyUp={escHandler}
                        autoFocus={true}
                        theme={inputTheme.header_search}
                        value={value}
                        onChangeAction={setSearch}
                        placeholder={commonClientResx.Search}
                        renderIcon={() => (
                            <InputIconWrapper>
                                <ButtonCross action={offSearchMode} title={commonClientResx.Close} />
                            </InputIconWrapper>
                        )}
                    />
                </InputWrapper>
            )}
            {!showButton && (
                <ButtonDefault action={setSearchMode} text={commonClientResx.Search} iconName={icon.search} />
            )}
        </>
    );
};

const useTrackSearch = (value: string) => {
    const trackEvent = useTrackEvent();
    const hasValue = !!value?.trim();
    useEffect(() => {
        if (hasValue) {
            trackEvent(commonAuditEvents.localSearchApplied);
        }
    }, [hasValue]);
};

const useOnContainerBlur = (callback: F) => {
    const elementRef = useRef<HTMLDivElement>();
    const onBlur: React.FocusEventHandler = e => {
        let focusingElement = e.relatedTarget as HTMLElement;
        const isFocusLost = !elementRef.current.contains(focusingElement);
        if (isFocusLost) {
            callback();
        }
    };

    return { elementRef, onBlur };
};
